@file:Suppress("ktlint:standard:no-wildcard-imports")

package xyz.malefic.multipage.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import xyz.malefic.multipage.components.Footer
import xyz.malefic.multipage.components.Header
import xyz.malefic.multipage.components.StickyContactBar
import xyz.malefic.multipage.i18n.t
import xyz.malefic.multipage.styles.Theme

@Page("/about")
@Composable
fun AboutPage() {
    Column(
        modifier = Modifier.fillMaxSize().backgroundColor(Theme.Background),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Header()
        AboutHeroSection()
        OurStorySection()
        OurValuesSection()
        CompanyInfoSection()
        AboutCtaSection()
        Footer()
        StickyContactBar()
    }
}

@Composable
private fun AboutHeroSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(400.px)
                .position(Position.Relative)
                .overflow(Overflow.Hidden),
        contentAlignment = Alignment.Center,
    ) {
        Image(
            src = "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
            modifier =
                Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Cover)
                    .position(Position.Absolute),
            alt = "",
        )

        Box(
            modifier =
                Modifier
                    .fillMaxSize()
                    .position(Position.Absolute)
                    .backgroundColor(rgba(25, 35, 50, 0.72f)),
        )

        Column(
            modifier =
                Modifier
                    .position(Position.Relative)
                    .maxWidth(700.px)
                    .padding(24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = t("about.hero.title"),
                modifier =
                    Modifier
                        .fontFamily(Theme.HeadingFontFamily)
                        .color(Theme.Surface)
                        .fontSize(44.px)
                        .fontWeight(FontWeight.Bold)
                        .margin(bottom = 20.px)
                        .textAlign(TextAlign.Center)
                        .letterSpacing(2.px),
            )

            SpanText(
                text = t("about.hero.subtitle"),
                modifier =
                    Modifier
                        .color(rgba(255, 255, 255, 0.88f))
                        .fontSize(18.px)
                        .textAlign(TextAlign.Center)
                        .lineHeight(1.8),
            )
        }
    }
}

@Composable
private fun OurStorySection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .maxWidth(800.px)
                .padding(leftRight = 24.px)
                .margin(topBottom = 96.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            title = t("about.story.title"),
            subtitle = "",
        )

        Column(
            modifier = Modifier.gap(24.px).margin(top = 40.px),
        ) {
            StoryParagraph(t("about.story.p1"))
            StoryParagraph(t("about.story.p2"))
            StoryParagraph(t("about.story.p3"))
        }
    }
}

@Composable
private fun StoryParagraph(text: String) {
    SpanText(
        text = text,
        modifier =
            Modifier
                .fontSize(16.px)
                .lineHeight(2.0)
                .color(Theme.TextPrimary)
                .textAlign(TextAlign.Center),
    )
}

@Composable
private fun OurValuesSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.SurfaceVariant),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(1000.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SectionHeading(
                title = t("about.values.title"),
                subtitle = "",
            )

            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .margin(top = 56.px)
                        .gap(28.px)
                        .flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Center,
            ) {
                ValueCard("\uD83D\uDE4F", t("about.values.respect.title"), t("about.values.respect.desc"))
                ValueCard("\uD83E\uDD1D", t("about.values.trust.title"), t("about.values.trust.desc"))
                ValueCard("\u2B50", t("about.values.quality.title"), t("about.values.quality.desc"))
                ValueCard("\uD83D\uDC9B", t("about.values.care.title"), t("about.values.care.desc"))
            }
        }
    }
}

@Composable
private fun ValueCard(
    icon: String,
    title: String,
    description: String,
) {
    Column(
        modifier =
            Modifier
                .width(220.px)
                .padding(28.px)
                .backgroundColor(Theme.Surface)
                .borderRadius(Theme.RadiusLg.px)
                .boxShadow(offsetY = 2.px, blurRadius = 10.px, spreadRadius = 0.px, color = Theme.ShadowColor),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier =
                Modifier
                    .size(56.px)
                    .backgroundColor(rgba(72, 90, 120, 0.07f))
                    .borderRadius(50.percent)
                    .margin(bottom = 18.px),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(icon, modifier = Modifier.fontSize(26.px))
        }

        SpanText(
            text = title,
            modifier =
                Modifier
                    .fontSize(18.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Theme.TextPrimary)
                    .textAlign(TextAlign.Center)
                    .margin(bottom = 12.px)
                    .letterSpacing(0.5.px),
        )

        SpanText(
            text = description,
            modifier =
                Modifier
                    .fontSize(14.px)
                    .color(Theme.TextSecondary)
                    .textAlign(TextAlign.Center)
                    .lineHeight(1.8),
        )
    }
}

@Composable
private fun CompanyInfoSection() {
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .maxWidth(700.px)
                .padding(leftRight = 24.px)
                .margin(topBottom = 96.px),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionHeading(
            title = t("about.info.title"),
            subtitle = "",
        )

        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .margin(top = 40.px)
                    .backgroundColor(Theme.Surface)
                    .borderRadius(Theme.RadiusLg.px)
                    .border(1.px, LineStyle.Solid, Theme.BorderLight)
                    .boxShadow(offsetY = 2.px, blurRadius = 10.px, spreadRadius = 0.px, color = Theme.ShadowColor)
                    .padding(40.px),
        ) {
            InfoRow(t("about.info.name"), t("about.info.name.value"))
            InfoDivider()
            InfoRow(t("about.info.service"), t("about.info.service.value"))
            InfoDivider()
            InfoRow(t("about.info.email"), t("about.info.email.value"))
            InfoDivider()
            InfoRow(t("about.info.address"), t("about.info.address.value"))
        }
    }
}

@Composable
private fun InfoRow(
    label: String,
    value: String,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 16.px)
                .gap(28.px)
                .flexWrap(FlexWrap.Wrap),
    ) {
        SpanText(
            text = label,
            modifier =
                Modifier
                    .width(130.px)
                    .fontSize(14.px)
                    .fontWeight(FontWeight.Bold)
                    .color(Theme.Primary),
        )
        SpanText(
            text = value,
            modifier =
                Modifier
                    .fontSize(15.px)
                    .color(Theme.TextPrimary)
                    .flexGrow(1)
                    .lineHeight(1.8),
        )
    }
}

@Composable
private fun InfoDivider() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .height(1.px)
                .backgroundColor(Theme.BorderLight),
    )
}

@Composable
private fun AboutCtaSection() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(topBottom = 96.px)
                .backgroundColor(Theme.Primary),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .maxWidth(700.px)
                    .padding(leftRight = 24.px),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                text = t("cta.title"),
                modifier =
                    Modifier
                        .fontFamily(Theme.HeadingFontFamily)
                        .color(Theme.Surface)
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 16.px)
                        .letterSpacing(1.px),
            )

            SpanText(
                text = t("cta.subtitle"),
                modifier =
                    Modifier
                        .color(rgba(255, 255, 255, 0.8f))
                        .fontSize(16.px)
                        .textAlign(TextAlign.Center)
                        .margin(bottom = 12.px)
                        .lineHeight(1.8),
            )

            SpanText(
                text = "info@livstayjapan.co.jp",
                modifier =
                    Modifier
                        .color(rgba(255, 255, 255, 0.6f))
                        .fontSize(14.px)
                        .margin(bottom = 40.px),
            )

            Box(
                modifier =
                    Modifier
                        .padding(leftRight = 48.px, topBottom = 18.px)
                        .backgroundColor(Theme.Secondary)
                        .borderRadius(Theme.RadiusMd.px)
                        .cursor(Cursor.Pointer)
                        .boxShadow(offsetY = 4.px, blurRadius = 16.px, color = rgba(180, 150, 100, 0.4f))
                        .onClick {
                            js("window.location.href = 'mailto:info@livstayjapan.co.jp'")
                        },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    text = t("cta.email"),
                    modifier =
                        Modifier
                            .fontWeight(FontWeight.Bold)
                            .color(Theme.Surface)
                            .fontSize(17.px)
                            .letterSpacing(1.px),
                )
            }
        }
    }
}
